/**
 * Activity Context
 * Manages live fleet activity events for the Dashboard Event Stream
 * Aggregates all real-time events (location, geofence, bus, schedule, maintenance)
 * into a unified chronological activity feed
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { realtimeClient, EVENTS } from '../services/realtimeClient';
import Logger from '../utils/logger';

const ActivityContext = createContext(null);

// Maximum number of activity events to keep in memory
const MAX_ACTIVITIES = 100;

// Counter for generating unique activity IDs (avoids duplicates in same millisecond)
let activityIdCounter = 0;

// Activity categories for filtering
export const ACTIVITY_CATEGORIES = {
  ALL: 'all',
  LOCATION: 'location',
  GEOFENCE: 'geofence',
  VEHICLE: 'vehicle',
  SCHEDULE: 'schedule',
  MAINTENANCE: 'maintenance',
  SYSTEM: 'system'
};

// Map WebSocket events to activity categories
const EVENT_CATEGORY_MAP = {
  [EVENTS.LOCATION_UPDATE]: ACTIVITY_CATEGORIES.LOCATION,
  [EVENTS.VEHICLE_LOCATION]: ACTIVITY_CATEGORIES.LOCATION,
  [EVENTS.BUS_LOCATION_UPDATE]: ACTIVITY_CATEGORIES.LOCATION,
  [EVENTS.GEOFENCE_ENTRY]: ACTIVITY_CATEGORIES.GEOFENCE,
  [EVENTS.GEOFENCE_EXIT]: ACTIVITY_CATEGORIES.GEOFENCE,
  [EVENTS.GEOFENCE_ALERT]: ACTIVITY_CATEGORIES.GEOFENCE,
  [EVENTS.BUS_STATUS_UPDATE]: ACTIVITY_CATEGORIES.VEHICLE,
  [EVENTS.BUS_CREATED]: ACTIVITY_CATEGORIES.VEHICLE,
  [EVENTS.BUS_UPDATED]: ACTIVITY_CATEGORIES.VEHICLE,
  [EVENTS.BUS_DELETED]: ACTIVITY_CATEGORIES.VEHICLE,
  [EVENTS.SCHEDULE_CREATED]: ACTIVITY_CATEGORIES.SCHEDULE,
  [EVENTS.SCHEDULE_UPDATED]: ACTIVITY_CATEGORIES.SCHEDULE,
  [EVENTS.SCHEDULE_DELETED]: ACTIVITY_CATEGORIES.SCHEDULE,
  [EVENTS.MAINTENANCE_CREATED]: ACTIVITY_CATEGORIES.MAINTENANCE,
  [EVENTS.MAINTENANCE_UPDATED]: ACTIVITY_CATEGORIES.MAINTENANCE,
  [EVENTS.MAINTENANCE_DELETED]: ACTIVITY_CATEGORIES.MAINTENANCE,
  [EVENTS.MAINTENANCE_ALERT]: ACTIVITY_CATEGORIES.MAINTENANCE,
  [EVENTS.DASHBOARD_REFRESH]: ACTIVITY_CATEGORIES.SYSTEM,
  [EVENTS.METRICS_UPDATE]: ACTIVITY_CATEGORIES.SYSTEM
};

// Event type display info
const EVENT_TYPE_INFO = {
  [EVENTS.LOCATION_UPDATE]: {
    icon: 'map-pin',
    label: 'Location Update',
    severity: 'info'
  },
  [EVENTS.VEHICLE_LOCATION]: {
    icon: 'map-pin',
    label: 'Vehicle Location',
    severity: 'info'
  },
  [EVENTS.BUS_LOCATION_UPDATE]: {
    icon: 'map-pin',
    label: 'Bus Location',
    severity: 'info'
  },
  [EVENTS.GEOFENCE_ENTRY]: {
    icon: 'log-in',
    label: 'Geofence Entry',
    severity: 'success'
  },
  [EVENTS.GEOFENCE_EXIT]: {
    icon: 'log-out',
    label: 'Geofence Exit',
    severity: 'info'
  },
  [EVENTS.GEOFENCE_ALERT]: {
    icon: 'alert-circle',
    label: 'Geofence Alert',
    severity: 'warning'
  },
  [EVENTS.BUS_STATUS_UPDATE]: {
    icon: 'truck',
    label: 'Bus Status',
    severity: 'info'
  },
  [EVENTS.BUS_CREATED]: {
    icon: 'plus-circle',
    label: 'Bus Added',
    severity: 'success'
  },
  [EVENTS.BUS_UPDATED]: {
    icon: 'edit',
    label: 'Bus Updated',
    severity: 'info'
  },
  [EVENTS.BUS_DELETED]: {
    icon: 'trash-2',
    label: 'Bus Removed',
    severity: 'warning'
  },
  [EVENTS.SCHEDULE_CREATED]: {
    icon: 'calendar-plus',
    label: 'Schedule Created',
    severity: 'success'
  },
  [EVENTS.SCHEDULE_UPDATED]: {
    icon: 'calendar',
    label: 'Schedule Updated',
    severity: 'info'
  },
  [EVENTS.SCHEDULE_DELETED]: {
    icon: 'calendar-x',
    label: 'Schedule Deleted',
    severity: 'warning'
  },
  [EVENTS.MAINTENANCE_CREATED]: {
    icon: 'wrench',
    label: 'Maintenance Created',
    severity: 'info'
  },
  [EVENTS.MAINTENANCE_UPDATED]: {
    icon: 'wrench',
    label: 'Maintenance Updated',
    severity: 'info'
  },
  [EVENTS.MAINTENANCE_DELETED]: {
    icon: 'wrench',
    label: 'Maintenance Removed',
    severity: 'info'
  },
  [EVENTS.MAINTENANCE_ALERT]: {
    icon: 'alert-triangle',
    label: 'Maintenance Alert',
    severity: 'warning'
  },
  [EVENTS.DASHBOARD_REFRESH]: {
    icon: 'refresh-cw',
    label: 'Dashboard Refresh',
    severity: 'info'
  },
  [EVENTS.METRICS_UPDATE]: {
    icon: 'bar-chart-2',
    label: 'Metrics Update',
    severity: 'info'
  }
};

/**
 * Transform raw event data into activity item
 */
function transformEventToActivity(eventType, data) {
  const typeInfo = EVENT_TYPE_INFO[eventType] || {
    icon: 'activity',
    label: 'Event',
    severity: 'info'
  };

  const category = EVENT_CATEGORY_MAP[eventType] || ACTIVITY_CATEGORIES.SYSTEM;

  // Generate title and description based on event type and data
  let title = typeInfo.label;
  let description = '';

  switch (eventType) {
    case EVENTS.LOCATION_UPDATE:
    case EVENTS.VEHICLE_LOCATION:
    case EVENTS.BUS_LOCATION_UPDATE:
      title = `${data.busNumber || data.vehicleNumber || 'Vehicle'} location updated`;
      description = data.speed !== null && data.speed !== undefined ? `Speed: ${Math.round(data.speed)} mph` : '';
      if (data.geofenceName) {
        description += description ? ` | ${data.geofenceName}` : data.geofenceName;
      }
      break;

    case EVENTS.GEOFENCE_ENTRY:
      title = `${data.busNumber || data.vehicleNumber || 'Vehicle'} entered ${data.geofenceName || 'geofence'}`;
      description = data.driverName ? `Driver: ${data.driverName}` : '';
      break;

    case EVENTS.GEOFENCE_EXIT:
      title = `${data.busNumber || data.vehicleNumber || 'Vehicle'} exited ${data.geofenceName || 'geofence'}`;
      description = data.driverName ? `Driver: ${data.driverName}` : '';
      break;

    case EVENTS.GEOFENCE_ALERT:
      title = `Geofence alert: ${data.geofenceName || 'Unknown'}`;
      description = `${data.busNumber || data.vehicleNumber || 'Vehicle'} - ${data.eventType || 'event'}`;
      break;

    case EVENTS.BUS_STATUS_UPDATE:
      title = `Bus ${data.busNumber || data.id} status: ${data.status || 'Unknown'}`;
      description = data.reason || '';
      break;

    case EVENTS.BUS_CREATED:
      title = `New bus added: ${data.busNumber || data.id}`;
      description = data.make && data.model ? `${data.make} ${data.model}` : '';
      break;

    case EVENTS.BUS_UPDATED:
      title = `Bus ${data.busNumber || data.id} updated`;
      description = '';
      break;

    case EVENTS.BUS_DELETED:
      title = `Bus ${data.busNumber || data.id} removed`;
      description = '';
      break;

    case EVENTS.SCHEDULE_CREATED:
      title = 'New schedule created';
      description = data.tripName || data.description || '';
      break;

    case EVENTS.SCHEDULE_UPDATED:
      title = 'Schedule updated';
      description = data.tripName || data.description || '';
      break;

    case EVENTS.SCHEDULE_DELETED:
      title = 'Schedule deleted';
      description = data.tripName || data.description || '';
      break;

    case EVENTS.MAINTENANCE_CREATED:
      title = 'Maintenance task created';
      description = data.description || data.busNumber || '';
      break;

    case EVENTS.MAINTENANCE_UPDATED:
      title = 'Maintenance task updated';
      description = data.description || data.busNumber || '';
      break;

    case EVENTS.MAINTENANCE_ALERT:
      title = `Maintenance alert: ${data.busNumber || 'Vehicle'}`;
      description = data.message || data.description || '';
      break;

    case EVENTS.DASHBOARD_REFRESH:
      title = 'Dashboard refreshed';
      description = data.reason || '';
      break;

    case EVENTS.METRICS_UPDATE:
      title = 'Metrics updated';
      description = data.type || '';
      break;

    default:
      description = JSON.stringify(data).substring(0, 100);
  }

  return {
    id: `activity-${Date.now()}-${++activityIdCounter}`,
    eventType,
    category,
    title,
    description,
    icon: typeInfo.icon,
    severity: typeInfo.severity,
    timestamp: data.timestamp || new Date().toISOString(),
    data
  };
}

export function ActivityProvider({ children }) {
  const [activities, setActivities] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [eventsReceived, setEventsReceived] = useState(0);

  /**
   * Subscribe to WebSocket connection status changes
   */
  useEffect(() => {
    const unsubscribe = realtimeClient.onConnectionStatusChange(status => {
      setIsConnected(status.isConnected);
      Logger.debug('Activity connection status changed', { isConnected: status.isConnected });
    });

    return () => unsubscribe();
  }, []);

  /**
   * Add a new activity to the feed
   */
  const addActivity = useCallback((eventType, data) => {
    const activity = transformEventToActivity(eventType, data);

    setActivities(prev => {
      // Add to beginning (newest first)
      const updated = [activity, ...prev];

      // Prune to max activities
      if (updated.length > MAX_ACTIVITIES) {
        return updated.slice(0, MAX_ACTIVITIES);
      }

      return updated;
    });

    setEventsReceived(prev => prev + 1);

    Logger.debug('Activity added', { eventType, title: activity.title });
  }, []);

  /**
   * Clear all activities
   */
  const clearActivities = useCallback(() => {
    setActivities([]);
    setEventsReceived(0);
  }, []);

  /**
   * Subscribe to WebSocket events
   */
  useEffect(() => {
    if (!realtimeClient.isInitialized) {
      Logger.debug('RealtimeClient not initialized, skipping activity subscription');
      return;
    }

    const unsubscribeFns = [];

    // Subscribe to all activity-relevant events
    const eventsToSubscribe = [
      // Location events
      EVENTS.LOCATION_UPDATE,
      EVENTS.VEHICLE_LOCATION,
      EVENTS.BUS_LOCATION_UPDATE,
      // Geofence events
      EVENTS.GEOFENCE_ENTRY,
      EVENTS.GEOFENCE_EXIT,
      EVENTS.GEOFENCE_ALERT,
      // Bus events
      EVENTS.BUS_STATUS_UPDATE,
      EVENTS.BUS_CREATED,
      EVENTS.BUS_UPDATED,
      EVENTS.BUS_DELETED,
      // Schedule events
      EVENTS.SCHEDULE_CREATED,
      EVENTS.SCHEDULE_UPDATED,
      EVENTS.SCHEDULE_DELETED,
      // Maintenance events
      EVENTS.MAINTENANCE_CREATED,
      EVENTS.MAINTENANCE_UPDATED,
      EVENTS.MAINTENANCE_DELETED,
      EVENTS.MAINTENANCE_ALERT,
      // System events
      EVENTS.DASHBOARD_REFRESH,
      EVENTS.METRICS_UPDATE
    ];

    eventsToSubscribe.forEach(eventType => {
      unsubscribeFns.push(
        realtimeClient.on(eventType, data => {
          addActivity(eventType, data);
        })
      );
    });

    setIsStreaming(true);
    Logger.info('Subscribed to activity events', { eventCount: eventsToSubscribe.length });

    // Cleanup on unmount
    return () => {
      unsubscribeFns.forEach(unsubscribe => unsubscribe());
      setIsStreaming(false);
      Logger.info('Unsubscribed from activity events');
    };
  }, [addActivity]);

  const value = useMemo(
    () => ({
      activities,
      isConnected,
      isStreaming,
      eventsReceived,
      addActivity,
      clearActivities,
      ACTIVITY_CATEGORIES
    }),
    [activities, isConnected, isStreaming, eventsReceived, addActivity, clearActivities]
  );

  return <ActivityContext.Provider value={value}>{children}</ActivityContext.Provider>;
}

/**
 * Hook to access activity context
 */
export function useActivity() {
  const context = useContext(ActivityContext);
  if (!context) {
    throw new Error('useActivity must be used within an ActivityProvider');
  }
  return context;
}

export default ActivityContext;
